import { useEffect, useState } from "react";
import { useModal } from "../context/ModalContext";
import { getVentureType } from "../services/valuesService";
import {
  getCompanyImportDetail,
  saveCompanyImportDetail,
  updateCompanyImportDetail,
  deleteCompanyImportDetail,
  getCompanyExportDetail,
  saveCompanyExportDetail,
  updateCompanyExportDetail,
  deleteCompanyExportDetail,
  saveAdditionalExportImportDetail,
  getAdditionalExportImportDetail,
  updateAdditionalExportImportDetail,
} from "../services/companyService";
import Loading from "./Loading";
import {
  Trash2,
  Save,
  Loader2,
  ArrowLeft,
  ChevronsRight,
  Plus,
} from "lucide-react";
import FormButton from "./FormActionButtons";
import { useToast } from "../context/ToastContext";
import FormNavigationButtons from "./FormNavigationButtons";

function CompanyTradeInformationForm({
  companyId,
  versionId = 1,
  onNext,
  onBack,
  isEditable,
  currentStep,
  totalSteps,
}) {
  const [ventureTypes, setVentureTypes] = useState([]);
  const [importDetails, setImportDetails] = useState([]);
  const [exportDetails, setExportDetails] = useState([]);
  const [initialImportDetails, setInitialImportDetails] = useState([]);
  const [initialExportDetails, setInitialExportDetails] = useState([]);
  const [initialAdditionalDetails, setInitialAdditionalDetails] = useState({});
  const [saving, setSaving] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [additionalDetails, setAdditionalDetails] = useState({
    frequentlyImportedProduct: "",
    frequentlyExportedProduct: "",
    frequentPurchaser: "",
    frequentCustomer: "",
    targetIndustries: "",
    targetRegions: "",
    supplyChainPartners: "",
  });
  const { showModal } = useModal();
  const [loading, setLoading] = useState(true);
  const [fallbackUsed, setFallbackUsed] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setLoading(true);
    setFallbackUsed(false);
    fetchVentureTypes();
    fetchImportDetails();
    fetchExportDetails();
    fetchAdditionalExportImportDetails();
    setLoading(false);
  }, [companyId, versionId]);

  const fetchVentureTypes = async () => {
    try {
      const data = await getVentureType();
      setVentureTypes(data);
    } catch (err) {
      console.error("Failed to fetch venture types", err);
    }
  };

  const fetchImportDetails = async () => {
    try {
      let fetchVersion = versionId;
      let isFallback = false;
      let data = await getCompanyImportDetail(companyId, fetchVersion);

      if ((!data || data.length === 0) && fetchVersion > 1) {
        fetchVersion = fetchVersion - 1;
        isFallback = true;
        data = await getCompanyImportDetail(companyId, fetchVersion);
      }

      if (!data || !data.length) {
        setImportDetails([createEmptyImportDetail()]);
        return;
      }
      data = data.map((x) => ({
        ...x,
        id: isFallback ? 0 : x.id,
        ventureType: x.ventureType?.id ?? x.ventureType, // fallback if already ID
        date: formatDateForInput(x.date),
      }));
      setImportDetails(data);
      setInitialImportDetails(JSON.stringify(data));

      setFallbackUsed((prev) => prev || isFallback);
    } catch (err) {
      console.error("Failed to fetch import details", err);
      setImportDetails([createEmptyImportDetail()]);
    }
  };

  const fetchExportDetails = async () => {
    try {
      let fetchVersion = versionId;
      let isFallback = false;
      let data = await getCompanyExportDetail(companyId, fetchVersion);

      if ((!data || data.length === 0) && fetchVersion > 1) {
        fetchVersion = fetchVersion - 1;
        isFallback = true;
        data = await getCompanyExportDetail(companyId, fetchVersion);
      }

      if (!data || !data.length) {
        setExportDetails([createEmptyExportDetail()]);
        return;
      }
      data = data.map((x) => ({
        ...x,
        id: isFallback ? 0 : x.id,
        ventureType: x.ventureType?.id ?? x.ventureType, // fallback if already ID
        date: formatDateForInput(x.date),
        customer: x.client,
      }));
      setExportDetails(data);
      setInitialExportDetails(JSON.stringify(data));

      setFallbackUsed((prev) => prev || isFallback);
    } catch (err) {
      console.error("Failed to fetch export details", err);
      setExportDetails([createEmptyExportDetail()]);
    }
  };

  const fetchAdditionalExportImportDetails = async () => {
    try {
      let fetchVersion = versionId;
      let isFallback = false;
      let data = await getAdditionalExportImportDetail(companyId, fetchVersion);

      if (
        (!data ||
          (!data.frequentImports?.length &&
            !data.frequentExports?.length &&
            !data.frequentPurchasers?.length &&
            !data.frequentCustomers?.length &&
            !data.targetIndustries?.length &&
            !data.targetRegions?.length &&
            !data.supplyChainPartners?.length)) &&
        fetchVersion > 1
      ) {
        fetchVersion = fetchVersion - 1;
        isFallback = true;
        data = await getAdditionalExportImportDetail(companyId, fetchVersion);
      }

      if (data) {
        setAdditionalDetails({
          frequentlyImportedProduct:
            data.frequentImports?.map((item) => item.value).join(", ") || "",
          frequentlyExportedProduct:
            data.frequentExports?.map((item) => item.value).join(", ") || "",
          frequentPurchaser:
            data.frequentPurchasers?.map((item) => item.value).join(", ") || "",
          frequentCustomer:
            data.frequentCustomers?.map((item) => item.value).join(", ") || "",
          targetIndustries:
            data.targetIndustries?.map((item) => item.value).join(", ") || "",
          targetRegions:
            data.targetRegions?.map((item) => item.value).join(", ") || "",
          supplyChainPartners:
            data.supplyChainPartners?.map((item) => item.value).join(", ") ||
            "",
        });
        setInitialAdditionalDetails(
          JSON.stringify({
            frequentlyImportedProduct:
              data.frequentImports?.map((item) => item.value).join(", ") || "",
            frequentlyExportedProduct:
              data.frequentExports?.map((item) => item.value).join(", ") || "",
            frequentPurchaser:
              data.frequentPurchasers?.map((item) => item.value).join(", ") ||
              "",
            frequentCustomer:
              data.frequentCustomers?.map((item) => item.value).join(", ") ||
              "",
            targetIndustries:
              data.targetIndustries?.map((item) => item.value).join(", ") || "",
            targetRegions:
              data.targetRegions?.map((item) => item.value).join(", ") || "",
            supplyChainPartners:
              data.supplyChainPartners?.map((item) => item.value).join(", ") ||
              "",
          })
        );
      }

      setFallbackUsed((prev) => prev || isFallback);
    } catch (err) {
      console.error("Failed to fetch additional export/import details", err);
    }
  };

  const createEmptyImportDetail = () => ({
    id: 0,
    date: "",
    product: "",
    client: "",
    contactNumber: "",
    destination: "",
    quantity: "",
    ventureType: ventureTypes[0]?.id || 1,
  });

  const createEmptyExportDetail = () => ({
    id: 0,
    date: "",
    product: "",
    customer: "",
    contactNumber: "",
    destination: "",
    quantity: "",
    ventureType: ventureTypes[0]?.id || 1,
  });

  const handleImportChange = (idx, field, value) => {
    const updated = [...importDetails];
    updated[idx] = { ...updated[idx], [field]: value };
    setImportDetails(updated);
    setIsChanged(true);
  };

  const handleExportChange = (idx, field, value) => {
    const updated = [...exportDetails];
    updated[idx] = { ...updated[idx], [field]: value };
    setExportDetails(updated);
    setIsChanged(true);
  };

  const handleAdditionalDetailChange = (field, value) => {
    setAdditionalDetails((prev) => ({ ...prev, [field]: value }));
    setIsChanged(true);
  };

  const addImportDetail = () =>
    setImportDetails((prev) => [...prev, createEmptyImportDetail()]);

  const addExportDetail = () =>
    setExportDetails((prev) => [...prev, createEmptyExportDetail()]);

  const handleSaveImportDetails = async () => {
    try {
      for (const detail of importDetails) {
        if (
          !detail.date ||
          !detail.product ||
          !detail.client ||
          !detail.destination ||
          !detail.contactNumber
        ) {
          throw new Error(
            "Please fill all required import fields before saving."
          );
        }

        if (!detail.quantity || parseInt(detail.quantity) <= 0) {
          throw new Error("Import quantity must be greater than 0.");
        }

        const payload = {
          companyId: parseInt(companyId),
          version: versionId,
          date: detail.date,
          product: detail.product,
          client: detail.client, // Changed 'client' to 'purchaser' to match UI
          contactNumber: detail.contactNumber, // Changed 'contactNumber' to 'contactInfo' to match UI
          destination: detail.destination,
          quantity: parseInt(detail.quantity),
          ventureType: detail.ventureType,
        };

        let res;
        if (detail.id && detail.id !== 0) {
          payload.id = detail.id;
          res = await updateCompanyImportDetail(payload);
        } else {
          res = await saveCompanyImportDetail(payload);
        }

        if (!res.status) {
          throw new Error("Failed to save import detail");
        }
      }
    } catch (err) {
      console.error("Error saving import details", err);
      // showModal(
      //   "Save Failed",
      //   err.message || "An error occurred while saving import details."
      // );
      toast.error(
        err.message || "An error occurred while saving import details."
      );
      throw err; // Re-throw to prevent further saving
    }
  };

  const handleSaveExportDetails = async () => {
    try {
      for (const detail of exportDetails) {
        if (
          !detail.date ||
          !detail.product ||
          !detail.customer ||
          !detail.destination ||
          !detail.contactNumber
        ) {
          throw new Error(
            "Please fill all required export fields before saving."
          );
        }

        if (!detail.quantity || parseInt(detail.quantity) <= 0) {
          throw new Error("Export quantity must be greater than 0.");
        }

        const payload = {
          companyId: parseInt(companyId),
          version: versionId,
          date: detail.date,
          product: detail.product,
          customer: detail.customer,
          contactNumber: detail.contactNumber, // Changed 'contactNumber' to 'contactInfo' to match UI
          destination: detail.destination,
          quantity: parseInt(detail.quantity),
          ventureType: detail.ventureType,
          client: detail.customer, // Changed 'client' to 'purchaser' to match UI
        };

        let res;
        if (detail.id && detail.id !== 0) {
          payload.id = detail.id;
          res = await updateCompanyExportDetail(payload);
        } else {
          res = await saveCompanyExportDetail(payload);
        }

        if (!res.status) {
          throw new Error("Failed to save export detail");
        }
      }
    } catch (err) {
      console.error("Error saving export details", err);
      // showModal(
      //   "Save Failed",
      //   err.message || "An error occurred while saving export details."
      // );
      toast.error(
        err.message || "An error occurred while saving import details."
      );
      throw err; // Re-throw to prevent further saving
    }
  };

  const handleSaveAdditionalDetails = async () => {
    try {
      const payload = {
        companyId: parseInt(companyId),
        version: versionId,
        ...additionalDetails,
        frequentImports: [additionalDetails.frequentlyImportedProduct],
        frequentExports: [additionalDetails.frequentlyExportedProduct],
        frequentPurchaser: [additionalDetails.frequentPurchaser],
        frequentCustomer: [additionalDetails.frequentCustomer],
        targetIndustries: [additionalDetails.targetIndustries],
        targetRegions: [additionalDetails.targetRegions],
        supplyChainPartners: [additionalDetails.supplyChainPartners],
      };

      let res;
      const existingDetails = await getAdditionalExportImportDetail(
        companyId,
        versionId
      );
      if (existingDetails && existingDetails.id) {
        payload.id = existingDetails.id;
        res = await updateAdditionalExportImportDetail(payload);
      } else {
        res = await saveAdditionalExportImportDetail(payload);
      }

      if (!res.status) {
        throw new Error("Failed to save additional export/import details");
      }
    } catch (err) {
      console.error("Error saving additional export/import details", err);
      // showModal(
      //   "Save Failed",
      //   err.message ||
      //     "An error occurred while saving additional export/import details."
      // );

      toast.error(
        err.message || "An error occurred while saving import details."
      );
      throw err; // Re-throw to prevent further saving
    }
  };

  const handleSave = async (onSuccessNext = null) => {
    try {
      setSaving(true);
      await handleSaveImportDetails();
      await handleSaveExportDetails();
      await handleSaveAdditionalDetails();

      setIsChanged(false);
      if (onSuccessNext) {
        onNext();
      } else {
        // showModal("Saved Successfully", "Trade information has been saved.");
        toast.success("Trade information has been saved.");
      }
    } catch (err) {
      // Errors from individual save functions are already handled,
      // no need to show a generic error here again.
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteImport = async (id, idx) => {
    if (!id) {
      setImportDetails((prev) => prev.filter((_, i) => i !== idx));
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this import entry?"
    );
    if (!confirmed) return;

    try {
      const res = await deleteCompanyImportDetail(id);

      if (!res.status) throw new Error("Failed to delete import detail");

      setImportDetails((prev) => prev.filter((_, i) => i !== idx));
      showModal("Success", "Import detail deleted successfully.");
    } catch (err) {
      console.error("Error deleting import detail", err);
      showModal("Error", "Failed to delete import detail.");
    }
  };

  const handleDeleteExport = async (id, idx) => {
    if (!id) {
      setExportDetails((prev) => prev.filter((_, i) => i !== idx));
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this export entry?"
    );
    if (!confirmed) return;

    try {
      const res = await deleteCompanyExportDetail(companyId, versionId, id);

      if (!res.status) throw new Error("Failed to delete export detail");

      setExportDetails((prev) => prev.filter((_, i) => i !== idx));
      showModal("Success", "Export detail deleted successfully.");
    } catch (err) {
      console.error("Error deleting export detail", err);
      showModal("Error", "Failed to delete export detail.");
    }
  };

  const handleSaveOnly = () => handleSave();

  const handleSaveAndContinue = () => {
    handleSave(() => {
      if (onNext) onNext();
    });
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";

    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const renderImportTable = () => (
    <div className="mb-6 overflow-x-auto">
      <h3 className="text-lg font-semibold mb-2">Import Details</h3>
      <table className="w-full border">
        <thead>
          <tr className="bg-gray-100">
            <th className="border px-2 py-2">Date</th>
            <th className="border px-2 py-2">Product</th>
            <th className="border px-2 py-2">Purchaser</th>
            <th className="border px-2 py-2">Contact Info</th>
            <th className="border px-2 py-2">Destination</th>
            <th className="border px-2 py-2">Quantity</th>
            <th className="border px-2 py-2">Venture Type</th>
            {isEditable && <th className="border px-2 py-2">Actions</th>}
          </tr>
        </thead>
        {isEditable && (
          <tbody>
            {importDetails.map((detail, idx) => (
              <tr key={idx}>
                <td className="border p-2">
                  <input
                    type="date"
                    className="w-full border rounded p-2"
                    value={detail.date}
                    onChange={(e) =>
                      handleImportChange(idx, "date", e.target.value)
                    }
                  />
                </td>
                <td className="border p-2">
                  <input
                    className="w-full border rounded p-2"
                    value={detail.product}
                    onChange={(e) =>
                      handleImportChange(idx, "product", e.target.value)
                    }
                  />
                </td>
                <td className="border p-2">
                  <input
                    className="w-full border rounded p-2"
                    value={detail.client}
                    onChange={(e) =>
                      handleImportChange(idx, "client", e.target.value)
                    }
                  />
                </td>
                <td className="border p-2">
                  <input
                    className="w-full border rounded p-2"
                    value={detail.contactNumber}
                    type="tel"
                    onChange={(e) =>
                      handleImportChange(idx, "contactNumber", e.target.value)
                    }
                  />
                </td>
                <td className="border p-2">
                  <input
                    className="w-full border rounded p-2"
                    value={detail.destination}
                    onChange={(e) =>
                      handleImportChange(idx, "destination", e.target.value)
                    }
                  />
                </td>
                <td className="border p-2">
                  <input
                    className="w-full border rounded p-2"
                    value={detail.quantity}
                    type="number"
                    onChange={(e) =>
                      handleImportChange(idx, "quantity", e.target.value)
                    }
                  />
                </td>
                <td className="border p-2">
                  <select
                    className="w-full border rounded p-2"
                    value={detail.ventureType}
                    onChange={(e) =>
                      handleImportChange(
                        idx,
                        "ventureType",
                        parseInt(e.target.value)
                      )
                    }
                  >
                    {ventureTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.value}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="items-center p-2 text-center">
                  <button
                    onClick={() => handleDeleteImport(detail.id, idx)}
                    className="text-red-500 cursor-pointer"
                  >
                    <Trash2 />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        )}
        {!isEditable && (
          <tbody>
            {importDetails.map((detail, idx) => (
              <tr key={idx}>
                <ReadOnlyCell value={detail.date} />
                <ReadOnlyCell value={detail.product} />
                <ReadOnlyCell value={detail.client} />
                <ReadOnlyCell value={detail.contactNumber} />
                <ReadOnlyCell value={detail.destination} />
                <ReadOnlyCell value={detail.quantity} />
                <ReadOnlyCell
                  value={
                    ventureTypes.find((x) => x.id === detail.ventureType).value
                  }
                />
              </tr>
            ))}
          </tbody>
        )}
      </table>
      {isEditable && (
        <FormButton
          icon={Plus}
          onClick={addImportDetail}
          variant="secondary"
          additionalClass="mt-4"
        >
          Add Import
        </FormButton>
      )}
    </div>
  );

  const renderExportTable = () => (
    <div className="mb-6 overflow-x-auto">
      <h3 className="text-lg font-semibold mb-2">Export Details</h3>
      <table className="w-full border">
        <thead>
          <tr className="bg-gray-100">
            <th className="border px-2 py-2">Date</th>
            <th className="border px-2 py-2">Product</th>
            <th className="border px-2 py-2">Customer</th>
            <th className="border px-2 py-2">Contact Info</th>
            <th className="border px-2 py-2">Destination</th>
            <th className="border px-2 py-2">Quantity</th>
            <th className="border px-2 py-2">Venture Type</th>
            {isEditable && <th className="border px-2 py-2">Actions</th>}
          </tr>
        </thead>
        {isEditable && (
          <tbody>
            {exportDetails.map((detail, idx) => (
              <tr key={idx}>
                <td className="border p-2">
                  <input
                    type="date"
                    className="w-full border rounded p-2"
                    value={detail.date}
                    onChange={(e) =>
                      handleExportChange(idx, "date", e.target.value)
                    }
                  />
                </td>
                <td className="border p-2">
                  <input
                    className="w-full border rounded p-2"
                    value={detail.product}
                    onChange={(e) =>
                      handleExportChange(idx, "product", e.target.value)
                    }
                  />
                </td>
                <td className="border p-2">
                  <input
                    className="w-full border rounded p-2"
                    value={detail.customer}
                    onChange={(e) =>
                      handleExportChange(idx, "customer", e.target.value)
                    }
                  />
                </td>
                <td className="border p-2">
                  <input
                    className="w-full border rounded p-2"
                    value={detail.contactNumber}
                    type="tel"
                    onChange={(e) =>
                      handleExportChange(idx, "contactNumber", e.target.value)
                    }
                  />
                </td>
                <td className="border p-2">
                  <input
                    className="w-full border rounded p-2"
                    value={detail.destination}
                    onChange={(e) =>
                      handleExportChange(idx, "destination", e.target.value)
                    }
                  />
                </td>
                <td className="border p-2">
                  <input
                    className="w-full border rounded p-2"
                    value={detail.quantity}
                    type="number"
                    onChange={(e) =>
                      handleExportChange(idx, "quantity", e.target.value)
                    }
                  />
                </td>
                <td className="border p-2">
                  <select
                    className="w-full border rounded p-2"
                    value={detail.ventureType}
                    onChange={(e) =>
                      handleExportChange(
                        idx,
                        "ventureType",
                        parseInt(e.target.value)
                      )
                    }
                  >
                    {ventureTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.value}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="items-center p-2 text-center">
                  <button
                    onClick={() => handleDeleteExport(detail.id, idx)}
                    className="text-red-500 cursor-pointer"
                  >
                    <Trash2 />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        )}
        {!isEditable && (
          <tbody>
            {exportDetails.map((detail, idx) => (
              <tr key={idx}>
                <ReadOnlyCell value={detail.date} />
                <ReadOnlyCell value={detail.product} />
                <ReadOnlyCell value={detail.client} />
                <ReadOnlyCell value={detail.contactNumber} />
                <ReadOnlyCell value={detail.destination} />
                <ReadOnlyCell value={detail.quantity} />
                <ReadOnlyCell
                  value={
                    ventureTypes.find((x) => x.id === detail.ventureType).value
                  }
                />
              </tr>
            ))}
          </tbody>
        )}
      </table>
      {isEditable && (
        <FormButton
          icon={Plus}
          onClick={addExportDetail}
          variant="secondary"
          additionalClass="mt-4"
        >
          Add Import
        </FormButton>
      )}
    </div>
  );

  const renderAdditionalInfo = () => (
    <div className="mb-6">
      <h3 className="text-lg font-semibold mb-2">
        Additional Export/Import Details
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Frequently Imported Product
          </label>
          <input
            type="text"
            value={additionalDetails.frequentlyImportedProduct}
            onChange={(e) =>
              handleAdditionalDetailChange(
                "frequentlyImportedProduct",
                e.target.value
              )
            }
            disabled={!isEditable}
            className={`mt-1 block w-full border border-gray-300 rounded-md  py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
              isEditable ? "" : "bg-gray-100"
            }`}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Frequently Exported Product
          </label>
          <input
            type="text"
            value={additionalDetails.frequentlyExportedProduct}
            onChange={(e) =>
              handleAdditionalDetailChange(
                "frequentlyExportedProduct",
                e.target.value
              )
            }
            disabled={!isEditable}
            className={`mt-1 block w-full border border-gray-300 rounded-md  py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
              isEditable ? "" : "bg-gray-100"
            }`}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Frequent Purchasers
          </label>
          <input
            type="text"
            value={additionalDetails.frequentPurchaser}
            onChange={(e) =>
              handleAdditionalDetailChange("frequentPurchaser", e.target.value)
            }
            disabled={!isEditable}
            className={`mt-1 block w-full border border-gray-300 rounded-md  py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
              isEditable ? "" : "bg-gray-100"
            }`}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Frequent Customers
          </label>
          <input
            type="text"
            value={additionalDetails.frequentCustomer}
            onChange={(e) =>
              handleAdditionalDetailChange("frequentCustomer", e.target.value)
            }
            disabled={!isEditable}
            className={`mt-1 block w-full border border-gray-300 rounded-md  py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
              isEditable ? "" : "bg-gray-100"
            }`}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Target Industries
          </label>
          <input
            type="text"
            value={additionalDetails.targetIndustries}
            onChange={(e) =>
              handleAdditionalDetailChange("targetIndustries", e.target.value)
            }
            disabled={!isEditable}
            className={`mt-1 block w-full border border-gray-300 rounded-md  py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
              isEditable ? "" : "bg-gray-100"
            }`}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Target Regions
          </label>
          <input
            type="text"
            value={additionalDetails.targetRegions}
            onChange={(e) =>
              handleAdditionalDetailChange("targetRegions", e.target.value)
            }
            disabled={!isEditable}
            className={`mt-1 block w-full border border-gray-300 rounded-md  py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
              isEditable ? "" : "bg-gray-100"
            }`}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Supply Chain Partners
          </label>
          <input
            type="text"
            value={additionalDetails.supplyChainPartners}
            onChange={(e) =>
              handleAdditionalDetailChange(
                "supplyChainPartners",
                e.target.value
              )
            }
            disabled={!isEditable}
            className={`mt-1 block w-full border border-gray-300 rounded-md  py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
              isEditable ? "" : "bg-gray-100"
            }`}
          />
        </div>
      </div>
    </div>
  );

  const ReadOnlyCell = ({ value }) => (
    <td className="py-2 px-4 border border-gray-300 text-sm text-gray-700">
      {value || <span className="text-gray-400">—</span>}
    </td>
  );

  return loading ? (
    <Loading fullscreen={false} />
  ) : (
    <section className="sm:px-1">
      <div className="bg-white p-6">
        <h2 className="text-xl font-semibold mb-6">Trade Information</h2>
        {renderImportTable()}
        {renderExportTable()}
        {renderAdditionalInfo()}

        {/* --- Buttons Section using the new component --- */}
        <FormNavigationButtons
          onBack={onBack}
          onNext={onNext}
          handleSaveOnly={handleSaveOnly}
          handleSaveAndContinue={handleSaveAndContinue}
          isEditable={isEditable}
          isChanged={isChanged}
          saving={saving}
          fallbackUsed={fallbackUsed}
          currentStep={currentStep} // Pass current step
          totalSteps={totalSteps} // Pass total steps
        />
        {/* --- End Buttons Section --- */}

        {/* Buttons */}
        <div className="flex flex-wrap gap-4 justify-between sm:justify-end mt-8 hidden">
          <button
            type="button"
            onClick={onBack}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 transition duration-200 cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {isEditable && (
            <button
              type="button"
              onClick={handleSaveOnly}
              disabled={!isChanged || saving}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
                saving || !isChanged
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
              }`}
            >
              {saving ? (
                <Loader2 className="animate-spin w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>
          )}

          {isEditable && (
            <button
              type="button"
              onClick={handleSaveAndContinue}
              disabled={!isChanged || saving}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
                saving || !isChanged
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 text-white cursor-pointer"
              }`}
            >
              {saving ? (
                <Loader2 className="animate-spin w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save & Continue
            </button>
          )}
          {/* {console.log(isChanged || saving || isEditable || !fallbackUsed)} */}
          <button
            type="button"
            onClick={onNext}
            disabled={saving || (isEditable && isChanged && !fallbackUsed)}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
              saving || (isEditable && isChanged && !fallbackUsed)
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-purple-600 hover:bg-purple-700 text-white cursor-pointer"
            }`}
          >
            <ChevronsRight className="w-4 h-4" />
            Next
          </button>
        </div>
      </div>
    </section>
  );
}

export default CompanyTradeInformationForm;
